<script setup lang="ts">
import LoanMasterDetail from '@/components/estate-objects/LoanMasterDetail.vue'
import type { PositionRow } from '@/components/finest-custom-position-list/types'
import { useEnumOptions } from '@/composables/useEnumOptions'
import { idOf, matchesBlankRow, usePersistedRows } from '@/composables/usePersistedRows'
import type { ExtendedEnum } from '@/types'
import { toNumber } from '@/utils/number'
import { requestJson } from '@/utils/postJson'
import LoanController from '@controllers/LoanController'
import { ulid } from 'ulid'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CollateralData = App.Data.EstateObject.CollateralData
import LoanData = App.Data.EstateObject.LoanData
import CollateralType = App.Enums.EstateObject.Loans.CollateralType
import FinancingType = App.Enums.EstateObject.Loans.FinancingType
import MezzanineType = App.Enums.EstateObject.Loans.MezzanineType

const props = defineProps<{
  estateObjectId: string
  loans: LoanData[]
  financingTypes: ExtendedEnum<FinancingType>[]
  mezzanineTypes: ExtendedEnum<MezzanineType>[]
  collateralTypes: ExtendedEnum<CollateralType>[]
}>()

const emit = defineEmits<{
  financingChange: [summary: { total: number; count: number }]
}>()

const { t } = useI18n({ useScope: 'local' })
const toEnumOptions = useEnumOptions()

const loanTypeOptions = computed(() => toEnumOptions(props.financingTypes, 'enums.financingType'))
const mezzanineTypeOptions = computed(() => toEnumOptions(props.mezzanineTypes, 'enums.mezzanineType'))
const collateralTypeOptions = computed(() => toEnumOptions(props.collateralTypes, 'enums.collateralType'))

const collaterals = ref<Record<string, CollateralData[]>>({})

watch(
  () => props.loans,
  (loans) => {
    collaterals.value = Object.fromEntries(loans.map((loan) => [loan.id ?? '', loan.collaterals ?? []]))
  },
  { immediate: true },
)

const setCollaterals = (loanKey: string, list: CollateralData[]): void => {
  collaterals.value = { ...collaterals.value, [loanKey]: list }
}

const yearsFromDuration = (duration: unknown): number | null => {
  const match = /^P(\d+)Y$/.exec(String(duration ?? ''))

  return match ? Number(match[1]) : null
}

const isoYears = (years: unknown): string | null => (typeof years === 'number' ? `P${years}Y` : null)

const toRow = (loan: LoanData): PositionRow => ({
  key: loan.id ?? '',
  id: loan.id ?? null,
  cells: {
    lender_name: loan.lender_name ?? null,
    loan_type: loan.loan_type ?? null,
    principal_amount: toNumber(loan.principal_amount),
    mortgage: toNumber(loan.mortgage),
    final_payment_amount: toNumber(loan.final_payment_amount),
    start_date: loan.start_date ?? null,
    repayment_start_date: loan.repayment_start_date ?? null,
    nominal_interest_rate: toNumber(loan.nominal_interest_rate),
    initial_repayment_rate: toNumber(loan.initial_repayment_rate),
    fixed_interest_rate_term: yearsFromDuration(loan.fixed_interest_rate_term),
    loan_term: yearsFromDuration(loan.loan_term),
    installment_amount: toNumber(loan.installment_amount),
    current_balance: toNumber(loan.current_balance),
    mezzanine_type: loan.mezzanine_detail?.mezzanine_type ?? null,
    exit_date: loan.mezzanine_detail?.exit_date ?? null,
  },
})

const loanCellKeys = [
  'lender_name',
  'loan_type',
  'principal_amount',
  'mortgage',
  'final_payment_amount',
  'start_date',
  'repayment_start_date',
  'nominal_interest_rate',
  'initial_repayment_rate',
  'fixed_interest_rate_term',
  'loan_term',
  'installment_amount',
  'current_balance',
  'mezzanine_type',
  'exit_date',
]

const blankLoan = (): PositionRow => ({
  key: ulid(),
  id: null,
  cells: Object.fromEntries(loanCellKeys.map((cell) => [cell, null])),
})

const toPayload = (row: PositionRow) => ({
  lender_name: row.cells.lender_name,
  loan_type: row.cells.loan_type,
  principal_amount: row.cells.principal_amount,
  mortgage: row.cells.mortgage,
  final_payment_amount: row.cells.final_payment_amount,
  start_date: row.cells.start_date,
  repayment_start_date: row.cells.repayment_start_date,
  nominal_interest_rate: row.cells.nominal_interest_rate,
  initial_repayment_rate: row.cells.initial_repayment_rate,
  fixed_interest_rate_term: isoYears(row.cells.fixed_interest_rate_term),
  loan_term: isoYears(row.cells.loan_term),
  mezzanine_detail:
    row.cells.mezzanine_type === null && row.cells.exit_date === null
      ? null
      : { mezzanine_type: row.cells.mezzanine_type, exit_date: row.cells.exit_date },
})

const applyCalculatedCells = (key: string, loan: LoanData): void => {
  rows.value = rows.value.map((row) =>
    row.key === key
      ? {
          ...row,
          cells: {
            ...row.cells,
            installment_amount: toNumber(loan.installment_amount),
            current_balance: toNumber(loan.current_balance),
          },
        }
      : row,
  )
}

const loanRoute = (id: string) => ({ estateObject: props.estateObjectId, loan: id })

const { rows, reset, addDraft, removeRow, ensureSaved, persistChanges, persistOnLeave } = usePersistedRows({
  toPayload,
  isDraft: matchesBlankRow(toPayload, blankLoan),
  create: async (payload, key) => {
    const loan = await requestJson<LoanData>('post', LoanController.store(props.estateObjectId).url, payload)

    applyCalculatedCells(key, loan)

    return idOf(loan)
  },
  update: async (id, payload, key) => {
    applyCalculatedCells(key, await requestJson<LoanData>('put', LoanController.update(loanRoute(id)).url, payload))
  },
  destroy: (id) => requestJson('delete', LoanController.destroy(loanRoute(id)).url, undefined),
  reloadProps: ['loans', 'fieldStates', 'completion'],
})

reset(props.loans.map(toRow))

watch(
  () => props.loans,
  (loans) => reset(loans.map(toRow)),
)

/** Loans are saved without an Inertia reload, so the totals shown above the table come from the live rows. */
watch(
  rows,
  (current) => {
    const total = current.reduce((sum, row) => sum + (toNumber(row.cells.principal_amount) ?? 0), 0)

    emit('financingChange', { total, count: current.length })
  },
  { deep: true, immediate: true },
)

const addLoan = (): void => addDraft(blankLoan())

defineExpose({ addLoan })
</script>

<template>
  <div @focusout="persistOnLeave">
    <LoanMasterDetail
      v-model:rows="rows"
      :title="t('title')"
      :estate-object-id="estateObjectId"
      :loan-type-options="loanTypeOptions"
      :mezzanine-type-options="mezzanineTypeOptions"
      :collateral-type-options="collateralTypeOptions"
      :collaterals="collaterals"
      :add-label="t('add')"
      :ensure-saved="ensureSaved"
      @collaterals-change="setCollaterals"
      @add-row="addLoan"
      @remove-row="removeRow"
      @commit="persistChanges" />
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Finanzierungen",
    "add": "Finanzierung hinzufügen"
  },
  "en": {
    "title": "Financing",
    "add": "Add financing"
  }
}
</i18n>
